import { useRef, useCallback } from 'react';

export interface UseLongPressOptions {
  /** Callback для короткого нажатия */
  onShortPress?: () => void;
  /** Callback для долгого нажатия */
  onLongPress?: () => void;
  /** Длительность для определения долгого нажатия (мс) */
  threshold?: number;
  /** Порог движения для отмены нажатия при скролле (px) */
  moveThreshold?: number;
  /** Включить вибрацию при долгом нажатии */
  vibrate?: boolean;
  /** Длительность вибрации (мс) */
  vibrateDuration?: number;
}

export interface UseLongPressHandlers {
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseUp: (e: React.MouseEvent) => void;
  onMouseLeave: () => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
  onTouchCancel: () => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
}

export interface UseLongPressResult {
  /** Обработчики событий для привязки к элементу */
  handlers: UseLongPressHandlers;
  /** Сбросить состояние (полезно при программном закрытии) */
  reset: () => void;
}

const DEFAULT_THRESHOLD = 500;
const DEFAULT_MOVE_THRESHOLD = 10;
const DEFAULT_VIBRATE_DURATION = 50;

/**
 * Хук для обработки коротких и долгих нажатий с защитой от скролла
 * 
 * @example
 * ```tsx
 * const { handlers } = useLongPress({
 *   onShortPress: () => openProfile(),
 *   onLongPress: () => openMenu(),
 * });
 * 
 * <div {...handlers}>Content</div>
 * ```
 */
export function useLongPress(options: UseLongPressOptions = {}): UseLongPressResult {
  const {
    onShortPress,
    onLongPress,
    threshold = DEFAULT_THRESHOLD,
    moveThreshold = DEFAULT_MOVE_THRESHOLD,
    vibrate = true,
    vibrateDuration = DEFAULT_VIBRATE_DURATION
  } = options;

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isLongPressRef = useRef(false);
  const isScrollingRef = useRef(false);
  const startPositionRef = useRef<{ x: number; y: number } | null>(null);
  const isPressedRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearTimer();
    isLongPressRef.current = false;
    isScrollingRef.current = false;
    startPositionRef.current = null;
    isPressedRef.current = false;
  }, [clearTimer]);

  const handlePressStart = useCallback((clientX: number, clientY: number) => {
    isPressedRef.current = true;
    isLongPressRef.current = false;
    isScrollingRef.current = false;
    startPositionRef.current = { x: clientX, y: clientY };

    timerRef.current = setTimeout(() => {
      // Проверяем, не был ли это скролл и всё ещё нажато
      if (isScrollingRef.current || !isPressedRef.current) return;

      isLongPressRef.current = true;
      
      // Вибрация для обратной связи
      if (vibrate && navigator.vibrate) {
        navigator.vibrate(vibrateDuration);
      }

      onLongPress?.();
    }, threshold);
  }, [threshold, vibrate, vibrateDuration, onLongPress]);

  const handlePressEnd = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    clearTimer();
    
    const wasPressed = isPressedRef.current;
    isPressedRef.current = false;
    startPositionRef.current = null;

    // Если не было нажатия - игнорируем
    if (!wasPressed) return;

    // Если это был скролл - игнорируем
    if (isScrollingRef.current) {
      isScrollingRef.current = false;
      return;
    }

    // Если это было долгое нажатие - меню уже открыто
    if (isLongPressRef.current) {
      e.preventDefault();
      isLongPressRef.current = false;
      return;
    }

    // Короткое нажатие
    onShortPress?.();
  }, [clearTimer, onShortPress]);

  const handleMove = useCallback((clientX: number, clientY: number) => {
    if (!startPositionRef.current || !isPressedRef.current) return;

    const deltaX = Math.abs(clientX - startPositionRef.current.x);
    const deltaY = Math.abs(clientY - startPositionRef.current.y);

    // Если движение превышает порог - это скролл
    if (deltaX > moveThreshold || deltaY > moveThreshold) {
      isScrollingRef.current = true;
      clearTimer();
    }
  }, [moveThreshold, clearTimer]);

  const handleCancel = useCallback(() => {
    clearTimer();
    isPressedRef.current = false;
    startPositionRef.current = null;
    isScrollingRef.current = false;
  }, [clearTimer]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    // Предотвращаем стандартное контекстное меню
    e.preventDefault();
    
    // Вызываем долгое нажатие если есть обработчик
    if (onLongPress) {
      clearTimer();
      isLongPressRef.current = true;
      
      if (vibrate && navigator.vibrate) {
        navigator.vibrate(vibrateDuration);
      }
      
      onLongPress();
    }
  }, [onLongPress, clearTimer, vibrate, vibrateDuration]);

  const handlers: UseLongPressHandlers = {
    onMouseDown: useCallback((e: React.MouseEvent) => {
      handlePressStart(e.clientX, e.clientY);
    }, [handlePressStart]),

    onMouseUp: useCallback((e: React.MouseEvent) => {
      handlePressEnd(e);
    }, [handlePressEnd]),

    onMouseLeave: handleCancel,

    onMouseMove: useCallback((e: React.MouseEvent) => {
      handleMove(e.clientX, e.clientY);
    }, [handleMove]),

    onTouchStart: useCallback((e: React.TouchEvent) => {
      const touch = e.touches[0];
      handlePressStart(touch.clientX, touch.clientY);
    }, [handlePressStart]),

    onTouchEnd: useCallback((e: React.TouchEvent) => {
      handlePressEnd(e);
    }, [handlePressEnd]),

    onTouchCancel: handleCancel,

    onTouchMove: useCallback((e: React.TouchEvent) => {
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
    }, [handleMove]),

    onContextMenu: handleContextMenu
  };

  return { handlers, reset };
}

export default useLongPress;
